<script setup lang="ts">
import { computed } from 'vue'
import { ICONS, type IconDefinition, type UiIconName } from './definitions'

/**
 * `<UiIcon>` — the only way an icon reaches the screen in this product.
 *
 * Colour comes from `currentColor`, so an icon is the colour of the text it
 * sits beside and needs no token of its own. Size is in `em` by default, so it
 * scales with the surrounding type instead of being pinned to a pixel value
 * that looks wrong in a 12px table cell and again in a 24px page title.
 *
 * Accessibility: an icon is decorative unless it is the *only* content of a
 * control, so `aria-hidden` is the default and a `label` opts into
 * `role="img"` with an accessible name. `<UiIconButton>` passes the label to
 * the button instead, which is why it does not set one here.
 */

const props = withDefaults(
  defineProps<{
    name: UiIconName
    /** Any CSS length. `1em` (default) ties the icon to the local font size. */
    size?: string | number
    /** Stroke weight on the 24-unit grid. 1.75 reads well from 14px to 32px. */
    strokeWidth?: number
    /**
     * Accessible name. Omit for a decorative icon that sits next to a text
     * label — announcing "download icon, Download" is noise.
     */
    label?: string
    /**
     * Forces mirroring under RTL on or off, overriding the icon's own
     * `directional` flag. Needed rarely: a check that means "forward" in a
     * stepper, a chevron used as a decorative bullet that must NOT flip.
     *
     * `undefined` means "not specified" and is why the default below is
     * declared explicitly: Vue casts an absent `boolean` prop to `false`
     * unless a default exists, which would make the `??` below dead code and
     * silently stop every directional icon from mirroring under RTL.
     */
    mirror?: boolean
  }>(),
  { size: '1em', strokeWidth: 1.75, mirror: undefined },
)

// `ICONS` is `as const`, so each entry narrows to its own literal shape and a
// lookup by a union key loses the common interface. Widening here restores it.
const definition = computed<IconDefinition>(() => ICONS[props.name])

const dimension = computed(() => (typeof props.size === 'number' ? `${props.size}px` : props.size))

const directional = computed(() => props.mirror ?? Boolean(definition.value.directional))
</script>

<template>
  <svg
    class="ui-icon"
    :class="{ 'ui-icon--directional': directional }"
    :width="dimension"
    :height="dimension"
    viewBox="0 0 24 24"
    :stroke-width="strokeWidth"
    stroke="currentColor"
    fill="none"
    stroke-linecap="round"
    stroke-linejoin="round"
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : 'true'"
    focusable="false"
  >
    <title v-if="label">{{ label }}</title>
    <path v-for="(d, index) in definition.paths ?? []" :key="`p${index}`" :d="d" />
    <circle
      v-for="(circle, index) in definition.circles ?? []"
      :key="`c${index}`"
      :cx="circle[0]"
      :cy="circle[1]"
      :r="circle[2]"
    />
    <rect
      v-for="(rect, index) in definition.rects ?? []"
      :key="`r${index}`"
      :x="rect[0]"
      :y="rect[1]"
      :width="rect[2]"
      :height="rect[3]"
      :rx="rect[4]"
    />
    <path
      v-for="(d, index) in definition.filled ?? []"
      :key="`f${index}`"
      :d="d"
      fill="currentColor"
      stroke="none"
    />
  </svg>
</template>

<style scoped>
.ui-icon {
  display: inline-block;
  flex: none;
  vertical-align: -0.125em;
  overflow: visible;
}

/* The mirror is declared here as well as in styles.css so an app that imports
   the component without the global stylesheet still gets correct RTL icons.

   NOT `:global([dir='rtl']) .ui-icon--directional`: @vue/compiler-sfc rewrites
   a `:global()` rule to the `:global` argument alone and discards the rest of
   the selector, so that form shipped as `[dir=rtl] { transform: scaleX(-1) }`
   — every element on the page mirrored, the whole Dhivehi portal included.
   The plain selector is correct: the scope attribute is appended to the last
   compound, which is this component's own root element. */
[dir='rtl'] .ui-icon--directional {
  transform: scaleX(-1);
}
</style>
